/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DomainFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for domain form.
────────────────────────────────────────────────────────────────*/

import { AlertCircle } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import CountryCodeSelect from '../../../components/ui/CountryCodeSelect';
import VoiceLanguageSelect from '../../../components/ui/VoiceLanguageSelect';
import TenantSelectSimple from '../../../components/ui/TenantSelectSimple';
import CertificateSelect from '../../../components/ui/CertificateSelect';
import type { DomainFormState, TenantOption } from '../types';
import type { CertificateOption } from '../hooks/useDomainData';

type DomainFormFieldKey = keyof DomainFormState;

interface DomainFormFieldsProps {
  formData: DomainFormState;
  errors: Partial<Record<DomainFormFieldKey, string>>;
  tenants: TenantOption[];
  loadingTenants: boolean;
  certificates: CertificateOption[];
  loadingCertificates: boolean;
  onInputChange: (field: keyof DomainFormState, value: DomainFormState[keyof DomainFormState]) => void;
  inputClasses: (hasError?: boolean) => string;
  formErrorKeys: DomainFormFieldKey[];
}

type TooltipLabelProps = {
  label: string;
  tooltip: string;
  required?: boolean;
  htmlFor?: string;
};

const TooltipLabel = ({ label, tooltip, required = false, htmlFor }: TooltipLabelProps) => (
  <SimpleTooltip content={tooltip}>
    <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
      {label}
      {required ? ' *' : ''}
    </label>
  </SimpleTooltip>
);

export function DomainFormFields({
  formData,
  errors,
  tenants,
  loadingTenants,
  certificates,
  loadingCertificates,
  onInputChange,
  inputClasses,
  formErrorKeys
}: DomainFormFieldsProps) {
  const { t } = useI18n();

  return (
    <>
      {/* 4-Column Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        {/* Row 1 */}
        <TooltipLabel
          htmlFor="domainName"
          label={t('telephonyDomains.domainName')}
          tooltip={t('telephonyDomains.tooltips.domainName')}
          required
        />
        <input
          id="domainName"
          type="text"
          value={formData.domainName}

          onChange={(e) => onInputChange('domainName', e.target.value)}
          className={inputClasses(!!errors.domainName)}
          placeholder={t('telephonyDomains.placeholders.domainName')}
          aria-invalid={!!errors.domainName}
          aria-required="true"
        />
        <TooltipLabel
          htmlFor="domainDescription"
          label={t('telephonyDomains.domainDescription')}
          tooltip={t('telephonyDomains.tooltips.domainDescription')}
        />
        <input
          id="domainDescription"
          type="text"
          value={formData.domainDescription}
          onChange={(e) => onInputChange('domainDescription', e.target.value)}
          className={inputClasses()}
          placeholder={t('telephonyDomains.placeholders.domainDescription')}
        />

        {/* Row 2 */}
        <TooltipLabel
          label={t('telephonyDomains.tenant')}
          tooltip={t('telephonyDomains.tooltips.tenant')}
          required
        />
        <TenantSelectSimple
          value={formData.tenantId ?? null}
          onChange={(value) => onInputChange('tenantId', value)}
          tenants={tenants}
          loading={loadingTenants}
          disabled={loadingTenants}
          showGlobalAccess={false}
          error={!!errors.tenantId}
        />
        <TooltipLabel
          label={t('telephonyDomains.language')}
          tooltip={t('telephonyDomains.tooltips.language')}
          required
        />
        <VoiceLanguageSelect
          value={formData.language}
          onChange={(value) => onInputChange('language', value)}
          placeholder={t('telephonyDomains.placeholders.language')}
        />

        {/* Row 3 */}
        <TooltipLabel
          label={t('telephonyDomains.timezone')}
          tooltip={t('telephonyDomains.tooltips.timezone')}
          required
        />
        <TimezoneSelect
          value={formData.timezone}
          onChange={(value) => onInputChange('timezone', value)}
          placeholder={t('telephonyDomains.placeholders.timezone')}
        />
        <TooltipLabel
          label={t('telephonyDomains.countryCode')}
          tooltip={t('telephonyDomains.tooltips.countryCode')}
          required
        />
        <CountryCodeSelect
          value={formData.countryCode}
          onChange={(value) => onInputChange('countryCode', value)}
          placeholder={t('telephonyDomains.placeholders.countryCode')}
        />

        {/* Row 4 - Certificate + Enabled */}
        <TooltipLabel
          label={t('telephonyDomains.certificate')}
          tooltip={t('telephonyDomains.tooltips.certificate')}
        />
        <div>
          <CertificateSelect
            value={formData.certificateId}
            onChange={(value) => onInputChange('certificateId', value)}
            certificates={certificates}
            loading={loadingCertificates}
            placeholder={t('telephonyDomains.placeholders.certificate')}
            searchPlaceholder={t('common.search', 'Search...')}
            noResultsText={t('common.noResults', 'No results found')}
            noDataText={t('common.noData', 'No data available')}
          />
        </div>

        <TooltipLabel
          label={t('telephonyDomains.serviceType', 'Service Type')}
          tooltip={t('telephonyDomains.tooltips.serviceType', 'Type of service for this domain: PBX (Class 5), Trunking (Class 4), or Hybrid')}
        />
        <select
          id="serviceType"
          value={formData.serviceType || 'pbx'}
          onChange={(e) => onInputChange('serviceType', e.target.value)}
          className={inputClasses()}
        >
          <option value="pbx">PBX (Class 5)</option>
          <option value="trunking">Trunking (Class 4)</option>
          <option value="hybrid">Hybrid</option>
        </select>

        <TooltipLabel
          label={t('telephonyDomains.enabled')}
          tooltip={t('telephonyDomains.tooltips.enabled')}
        />
        <div className="flex justify-start">
          <Toggle
            value={formData.domainEnabled}

            onChange={(value) => onInputChange('domainEnabled', value)}
          />
        </div>
      </div>

      {/* Error Messages */}
      <div className="mt-4 space-y-2">
        {formErrorKeys.map((key) =>
          errors[key] ? (
            <p key={key} className="flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="h-3 w-3" />
              {errors[key]}
            </p>
          ) : null
        )}
      </div>
    </>
  );
}


